import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterPills, type FilterPillGroup } from '../FilterPills';
import { FilterSearch } from '../FilterSearch';
import { useToast, toastFromError } from '../ToastProvider';
import { useZoneReadReport } from '../ZoneContext';
import { ZoneCell, ZoneFilter } from '../ZoneIndicators';
import { ZONE_TABLE, consoleZoneLabel, HOME_ZONE } from '../../lib/zones';
import { useStaffMe } from '../../hooks/useStaffMe';
import { useReAuth, ReauthCancelledError } from '../../lib/reauth';
import { formatInstant, useEffectiveTimeZone } from '../../lib/time-format';
import { ActionConfirmModal } from '../../routes/admin/ActionConfirmModal';
import './telemetry-flags.css';
import {
  listTelemetryFlags, correctTelemetryFlag, keepTelemetryFlag,
  APPLICABLE_SOURCES, FLAG_REASON_LABEL, FLAG_SOURCE_LABEL,
  type FlagReason, type FlagSource, type FlagStatus, type TelemetryFlagRow,
} from '../../lib/telemetry-flags-api';
import { ActionIcon, ActionsRow } from '../ActionIcon';

/**
 * Monitoring › Telemetry flags — owners telling us a Live visits row is wrong (the accuracy loop,
 * 27 October 2026; design prompt 24-02 §C4; lambdas/reporting/src/visit-flags.ts).
 *
 * The ops list standard: FilterSearch, FilterPills with live counts (status, and reason), a Zone
 * column and a per-zone legend that is always visible. Each row shows the visit as the owner saw it,
 * what the owner says, and what the snippet saw. The eye opens the full provenance; **Correct**
 * applies the owner's source (or moves the visit out as their team / a bot) — in Live visits from
 * the next read and in the monthly journeys from the next recompute — and **Keep** answers with a
 * one-line reply the owner sees on the row. Both sit behind an ActionConfirmModal and the step-up
 * re-auth, and are hidden from roles that cannot change customer data (customers:manage =
 * nexa_admin, super_admin); support sees the list read-only.
 */
const STATUS_LABEL: Record<FlagStatus, string> = { open: 'Open', corrected: 'Corrected', kept: 'Kept' };
const REASONS = Object.keys(FLAG_REASON_LABEL) as FlagReason[];
const STATUSES: FlagStatus[] = ['open', 'corrected', 'kept'];
const ROW_SOURCE: Record<string, string> = {
  direct: 'Direct', google: 'Google search', facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', other: 'Other referrer',
};

function fmtDur(t: number): string {
  const s = Math.max(0, Math.round(t || 0));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/** "What the snippet saw", one short line per fact. */
export function snippetSaw(e: Record<string, unknown> | null): string[] {
  if (!e) return ['Not recorded'];
  const s = (k: string) => (typeof e[k] === 'string' ? (e[k] as string) : '');
  const tags = [['utm_source', s('utmSource')], ['utm_medium', s('utmMedium')], ['utm_campaign', s('utmCampaign')]].filter(([, v]) => v).map(([k, v]) => `${k}=${v}`);
  const ids = [e.hasInstagramShareTag ? 'igsh' : '', e.hasFbclid ? 'fbclid' : '', e.hasGclid ? 'gclid' : ''].filter(Boolean);
  return [
    s('referrerHost') ? `Referrer ${s('referrerHost')}` : 'No referrer',
    tags.length ? tags.join(' · ') : 'No campaign tags',
    ids.length ? `On the link: ${ids.join(', ')}` : '',
    s('inferredSource') ? `In-app browser: ${s('inferredSource')}` : '',
    [s('device'), s('browser')].filter(Boolean).join(' · '),
    [s('region'), s('country')].filter(Boolean).join(', '),
    s('channel') ? `Classified ${s('channel')}${s('origin') ? ` × ${s('origin')}` : ''}` : '',
  ].filter(Boolean);
}

type Dialog = { kind: 'correct' | 'keep'; row: TelemetryFlagRow } | null;

export function TelemetryFlagsTab() {
  const toast = useToast();
  const reauth = useReAuth();
  const me = useStaffMe();
  const viewerZone = useEffectiveTimeZone();
  const canMutate = me.status === 'loaded' && (me.me.role === 'super_admin' || me.me.role === 'nexa_admin');

  const [rows, setRows] = useState<TelemetryFlagRow[] | null>(null);
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [statuses, setStatuses] = useState<Set<FlagStatus>>(new Set(STATUSES));
  const [reasons, setReasons] = useState<Set<FlagReason>>(new Set(REASONS));
  const [open, setOpen] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  // Correct: apply a source, or move the visit out. Keep: the reply.
  const [applyKind, setApplyKind] = useState<'source' | 'team' | 'bot'>('source');
  const [applySource, setApplySource] = useState<Exclude<FlagSource, 'other'>>('direct');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await listTelemetryFlags();
      setRows(r.rows);
      setFailedZones(r.failedZones);
    } catch (e) {
      setRows([]);
      toast.push({ variant: 'error', ...toastFromError(e, 'Couldn’t load the telemetry flags') });
    }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  useZoneReadReport({ rows, failedZones, filter: zoneFilter });
  const all = useMemo(() => rows ?? [], [rows]);
  const legend = useMemo(() => ZONE_TABLE.map((z) => ({
    code: z.code, label: consoleZoneLabel(z.code), n: all.filter((r) => (r.zone ?? HOME_ZONE.code) === z.code).length,
  })), [all]);
  const statusPills: FilterPillGroup<FlagStatus>[] = STATUSES.map((k) => ({ key: k, label: STATUS_LABEL[k], count: all.filter((r) => r.status === k).length }));
  const reasonPills: FilterPillGroup<FlagReason>[] = REASONS.map((k) => ({ key: k, label: FLAG_REASON_LABEL[k], count: all.filter((r) => r.reason === k).length }));
  const q = query.trim().toLowerCase();
  const shown = all.filter((r) => (zoneFilter === 'all' || (r.zone ?? HOME_ZONE.code) === zoneFilter)
    && statuses.has(r.status) && reasons.has(r.reason)
    && (!q || [r.customer, r.hostname, r.text, r.suggestedSource ? FLAG_SOURCE_LABEL[r.suggestedSource] : '', FLAG_REASON_LABEL[r.reason]]
      .some((t) => (t || '').toLowerCase().includes(q))));

  const openDialog = (kind: 'correct' | 'keep', row: TelemetryFlagRow) => {
    setNote('');
    if (kind === 'correct') {
      if (row.reason === 'me_or_team') setApplyKind('team');
      else if (row.reason === 'bot') setApplyKind('bot');
      else setApplyKind('source');
      const s = row.suggestedSource;
      setApplySource(s && s !== 'other' ? s : 'direct');
    }
    setDialog({ kind, row });
  };

  const confirm = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      if (dialog.kind === 'correct') {
        const input = applyKind === 'source'
          ? { kind: 'source' as const, source: applySource, ...(note.trim() ? { note: note.trim() } : {}) }
          : { kind: 'exclude' as const, as: applyKind, ...(note.trim() ? { note: note.trim() } : {}) };
        await reauth.withReauth(() => correctTelemetryFlag(dialog.row, input));
        toast.push({ variant: 'success', title: 'Correction applied', detail: 'Live visits shows it now; the monthly journeys pick it up at the next recompute.' });
      } else {
        await reauth.withReauth(() => keepTelemetryFlag(dialog.row, note.trim()));
        toast.push({ variant: 'success', title: 'Kept as is — the owner sees your reply on the row' });
      }
      setDialog(null);
      await load();
    } catch (e) {
      if (!(e instanceof ReauthCancelledError)) {
        toast.push({ variant: 'error', ...toastFromError(e, dialog.kind === 'correct' ? 'Couldn’t apply the correction' : 'Couldn’t keep the visit') });
      }
    } finally {
      setBusy(false);
    }
  };

  const whenIn = (iso: string, zone: string) => {
    const z = zone || viewerZone;
    return `${formatInstant(iso, z, 'datetime').text} · ${z}`;
  };

  return (
    <div data-testid="telemetry-flags-tab">
      <p className="tf-intro">Owners telling us a Live visits row is wrong. Correct applies their source to that visit (Live visits now, the monthly journeys at the next recompute) and records a labelled example; Keep answers them on the row.</p>
      {failedZones.length > 0 && (
        <div className="error-banner" role="alert">{failedZones.join(', ')} did not respond — its flags are missing from this list.</div>
      )}
      <table className="alerts-zone-legend" data-testid="telemetry-flags-zone-legend" aria-label="Telemetry flags by zone">
        <thead><tr><th scope="col">Zone</th><th scope="col">Flags</th></tr></thead>
        <tbody>
          {legend.map((r) => (<tr key={r.code} data-zone-row={r.code}><th scope="row"><ZoneCell zone={r.code} /></th><td>{rows ? r.n : '…'}</td></tr>))}
        </tbody>
      </table>
      {/* The ops list standard's own container — FilterSearch is styled only inside it (User Management, Partners). */}
      <div className="portal-applications-controls">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search customer, domain or what the owner said…" ariaLabel="Search telemetry flags" />
        <FilterPills groups={statusPills} selected={statuses} onChange={setStatuses} ariaLabel="Flag status" />
        <FilterPills groups={reasonPills} selected={reasons} onChange={setReasons} ariaLabel="What the owner said" />
      </div>
      <div className="alerts-filters">
        <div className="alerts-controls">
          <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="alerts-zone-filter" />
          <button type="button" className="alerts-fpill" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      {rows === null ? (
        <div role="status" aria-live="polite">
          <div className="skeleton-row" aria-hidden="true" /><div className="skeleton-row" aria-hidden="true" />
          <p className="muted">Loading the telemetry flags…</p>
        </div>
      ) : shown.length === 0 ? (
        <p className="muted" data-testid="telemetry-flags-empty">{all.length === 0 ? 'No owner has flagged a visit yet.' : 'No flags match these filters.'}</p>
      ) : (
        <table className="alerts-table" data-testid="telemetry-flags-table">
          <thead>
            <tr>
              <th scope="col">Customer</th><th scope="col">Domain</th><th scope="col">When (owner’s zone)</th>
              <th scope="col">Row as shown</th><th scope="col">Owner says</th><th scope="col">Snippet saw</th>
              <th scope="col">Zone</th><th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const key = `${r.zone}|${r.domainToken}|${r.visitId}`;
              const row = r.row;
              return [
                <tr key={key} data-testid="telemetry-flag-row" data-status={r.status}>
                  <td>{r.customer || '—'}</td>
                  <td>{r.hostname || r.domainToken}</td>
                  <td>{row ? whenIn(row.startedAt, r.ownerTimeZone) : '—'}<br /><span className="muted">flagged {whenIn(r.createdAt, r.ownerTimeZone)}</span></td>
                  <td title={row?.why ?? ''}>{row ? `${row.source ? ROW_SOURCE[row.source] ?? row.source : 'Machine read'} · ${row.landing} · ${row.pageCount} page${row.pageCount === 1 ? '' : 's'} · ${fmtDur(row.durationSeconds)}` : '—'}</td>
                  <td>
                    <b>{FLAG_REASON_LABEL[r.reason]}</b>
                    {r.suggestedSource ? <> — {FLAG_SOURCE_LABEL[r.suggestedSource]}</> : null}
                    {r.text ? <><br />“{r.text}”</> : null}
                  </td>
                  <td>{snippetSaw(r.evidence).slice(0, 2).join(' · ')}</td>
                  <td><ZoneCell zone={r.zone} /></td>
                  <td>
                    <span className={`tf-pill tf-${r.status}`}>{STATUS_LABEL[r.status]}</span>{' '}
                    {/* §6 rule (b) — icon buttons with their own tooltips, side by side (31 October 2026). */}
                    <ActionsRow>
                      <ActionIcon act="view" icon="view" label="See the provenance" tip="See the provenance — what the snippet saw"
                        expanded={open === key} testId="telemetry-flag-eye"
                        onClick={() => setOpen((o) => (o === key ? null : key))} />
                      {canMutate && r.status === 'open' && (
                        <>
                          <ActionIcon act="review" icon="review" label="Correct" tip="Correct — apply the owner's source to this visit"
                            testId="telemetry-flag-correct" onClick={() => openDialog('correct', r)} />
                          <ActionIcon act="keep" icon="keep" label="Keep" tip="Keep — the row is right; answer the owner"
                            testId="telemetry-flag-keep" onClick={() => openDialog('keep', r)} />
                        </>
                      )}
                    </ActionsRow>
                  </td>
                </tr>,
                open === key ? (
                  <tr key={`${key}-p`} className="telemetry-flag-prov"><td colSpan={8}>
                    <dl className="telemetry-flag-facts" data-testid="telemetry-flag-provenance">
                      <div><dt>Shown to the owner</dt><dd>{row ? `${row.source ? ROW_SOURCE[row.source] ?? row.source : 'Machine read'} — ${row.why || '—'}` : '—'}</dd></div>
                      <div><dt>Snippet saw</dt><dd>{snippetSaw(r.evidence).join(' · ')}</dd></div>
                      <div><dt>Visit</dt><dd>{row ? `${row.landing} · ${row.pageCount} pages · ${row.clickCount} clicks · ${fmtDur(row.durationSeconds)} · ${[row.device, row.browser].filter(Boolean).join(' · ')}` : '—'}</dd></div>
                      {r.status !== 'open' && <div><dt>Review</dt><dd>{STATUS_LABEL[r.status]} by {r.reviewer || '—'} at {r.reviewedAt ? whenIn(r.reviewedAt, viewerZone) : '—'}{r.reply ? ` — “${r.reply}”` : ''}</dd></div>}
                    </dl>
                  </td></tr>
                ) : null,
              ];
            })}
          </tbody>
        </table>
      )}

      <ActionConfirmModal
        open={dialog?.kind === 'correct'}
        title="Correct this visit"
        intent="primary"
        primaryLabel="Apply correction"
        loading={busy}
        onConfirm={() => void confirm()}
        onCancel={() => setDialog(null)}
      >
        {dialog?.kind === 'correct' && (
          <div className="telemetry-flag-form">
            <p>{dialog.row.customer || dialog.row.hostname}: {FLAG_REASON_LABEL[dialog.row.reason]}{dialog.row.suggestedSource ? ` — ${FLAG_SOURCE_LABEL[dialog.row.suggestedSource]}` : ''}{dialog.row.text ? ` (“${dialog.row.text}”)` : ''}.</p>
            <label><input type="radio" name="tf-kind" checked={applyKind === 'source'} onChange={() => setApplyKind('source')} /> Apply a source</label>
            {applyKind === 'source' && (
              <select value={applySource} onChange={(e) => setApplySource(e.target.value as Exclude<FlagSource, 'other'>)} aria-label="Source to apply" data-testid="telemetry-flag-apply-source">
                {APPLICABLE_SOURCES.map((s) => <option key={s} value={s}>{FLAG_SOURCE_LABEL[s]}</option>)}
              </select>
            )}
            <label><input type="radio" name="tf-kind" checked={applyKind === 'team'} onChange={() => setApplyKind('team')} /> It was the owner’s own team — don’t count it</label>
            <label><input type="radio" name="tf-kind" checked={applyKind === 'bot'} onChange={() => setApplyKind('bot')} /> It was a bot — don’t count it as a person</label>
            <label className="telemetry-flag-note">Note (optional)<textarea value={note} maxLength={200} rows={2} onChange={(e) => setNote(e.target.value)} /></label>
          </div>
        )}
      </ActionConfirmModal>
      <ActionConfirmModal
        open={dialog?.kind === 'keep'}
        title="Keep this visit as it is"
        intent="primary"
        primaryLabel="Keep with reply"
        primaryDisabled={!note.trim()}
        loading={busy}
        onConfirm={() => void confirm()}
        onCancel={() => setDialog(null)}
      >
        {dialog?.kind === 'keep' && (
          <label className="telemetry-flag-note">One line to the owner — they see it on the row
            <textarea value={note} maxLength={200} rows={2} onChange={(e) => setNote(e.target.value)} data-testid="telemetry-flag-reply"
              placeholder="e.g. No referrer was sent — Instagram's app hid it, so it stays Direct." />
          </label>
        )}
      </ActionConfirmModal>
    </div>
  );
}
